import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, InjectionToken } from '@angular/core';

import { environment } from '../../../../environments/environment';
import { UsuarioPerfil } from '../../../home/usuario/usuario-perfil';

export type GtagFn = (...args: unknown[]) => void;

export interface IWindowWithGtag extends Window {
  dataLayer?: unknown[];
  gtag?: GtagFn;
}

/** Measurement ID do GA4, resolvido do environment. Token dedicado para que o DI injete a string (primitivos não são injetáveis por tipo). */
export const GA_MEASUREMENT_ID = new InjectionToken<string>('GA_MEASUREMENT_ID', {
  providedIn: 'root',
  factory: () => environment.gaMeasurementId,
});

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(GA_MEASUREMENT_ID) private readonly measurementId: string,
  ) {}

  private get isEnabled(): boolean {
    return !!this.measurementId;
  }

  private get window(): IWindowWithGtag | null {
    return this.document.defaultView as IWindowWithGtag | null;
  }

  init(): void {
    const win = this.window;
    if (!this.isEnabled || !win || win.gtag) {
      return;
    }

    this.injectScript();

    const dataLayer = win.dataLayer || [];
    win.dataLayer = dataLayer;
    /* eslint-disable prefer-arrow/prefer-arrow-functions, prefer-rest-params, space-before-function-paren */
    const gtag: GtagFn = function() {
      dataLayer.push(arguments);
    };
    /* eslint-enable prefer-arrow/prefer-arrow-functions, prefer-rest-params, space-before-function-paren */
    win.gtag = gtag;

    gtag('js', new Date());
    /* eslint-disable @typescript-eslint/naming-convention -- payload do gtag.js usa snake_case */
    gtag('config', this.measurementId, {
      send_page_view: false,
      user_properties: { tenant: this.getTenant(win) },
    });
    /* eslint-enable @typescript-eslint/naming-convention */
  }

  /** Registra a navegação de uma rota do SPA como page_view. */
  trackPageView(url: string): void {
    const win = this.window;
    if (!this.isEnabled || !win || !win.gtag) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention -- payload do gtag.js usa snake_case
    win.gtag('event', 'page_view', { page_path: url });
  }

  /**
   * Registra um evento customizado no GA4 (ex: `exportou_relatorio`, `filtro_aplicado`).
   * Os parâmetros só viram colunas relatáveis após serem registrados como dimensão
   * personalizada no painel do GA4 (Admin → Definições personalizadas).
   */
  trackEvent(name: string, params?: Record<string, unknown>): void {
    const win = this.window;
    if (!this.isEnabled || !win || !win.gtag) {
      return;
    }

    win.gtag('event', name, params);
  }

  /** Define o perfil do usuário como user property no GA4. Requer registrar `user_profile`/`user_profile_id` como dimensão personalizada (escopo Usuário) no painel para filtrar. */
  setUserProfile(perfil: UsuarioPerfil | null): void {
    const win = this.window;
    if (!this.isEnabled || !win || !win.gtag) {
      return;
    }

    /* eslint-disable @typescript-eslint/naming-convention -- payload do gtag.js usa snake_case */
    win.gtag('set', 'user_properties', {
      user_profile: perfil ? perfil.nome : null,
      user_profile_id: perfil ? String(perfil.id) : null,
    });
    /* eslint-enable @typescript-eslint/naming-convention */
  }

  /** Injeta o script async do gtag.js no <head>. */
  private injectScript(): void {
    const script = this.document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
    this.document.head.appendChild(script);
  }

  /**
   * Identifica o tenant. deriva do subdomínio (ex: `mmc.sogivendas.com.br` -> `mmc`).
   * Isolado aqui para no futuro derivar do JWT sem alterar o restante do service.
   */
  private getTenant(win: IWindowWithGtag): string {
    const hostname = win.location.hostname;
    const [subdomain] = hostname.split('.');
    return subdomain || hostname;
  }
}
